import React from "react";
import { usePermissions } from "../../../../utils/permissions";

const normalizeKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const resolveTipoReferido = (clave) => {
  if (!clave) return "";
  if (clave === "docente") return "docente";
  if (["estudiante", "administrativo", "externo"].includes(clave)) return clave;
  return "";
};

export default function BitacoraModal({
  show,
  onClose,
  tabActiva,
  setTabActiva,
  editando,
  nuevoRegistro,
  setNuevoRegistro,
  fuentesLista,
  empresasLista,
  categoriasLista,
  subcategoriasLista,
  accionesLista,
  selectedCategoria,
  setSelectedCategoria,
  selectedSubcategoria,
  setSelectedSubcategoria,
  selectedAccion,
  setSelectedAccion,
  requiereReferido,
  fuenteHabilitaReferido,
  fuenteHabilitaEmpresa,
  referidosOpciones,
  referidoSearchTerm,
  setReferidoSearchTerm,
  cargandoReferidos,
  guardando,
  showTransfer,
  showPreinscribir,
  handleGuardar,
  buscarInteresado,
  bloquearInteresado,
  documentoBloqueado,
  setBloquearInteresado,
}) {
  const { hasPermission } = usePermissions();
  const puedeEditarFecha = hasPermission("migracion.fecha_retroactiva");

  const [referidoMenuAbierto, setReferidoMenuAbierto] = React.useState(false);
  const [empresaMenuAbierto, setEmpresaMenuAbierto] = React.useState(false);
  const [empresaSearchTerm, setEmpresaSearchTerm] = React.useState("");

  React.useEffect(() => {
    if (!show) {
      setReferidoMenuAbierto(false);
      setEmpresaMenuAbierto(false);
      setEmpresaSearchTerm("");
    }
  }, [show]);

  React.useEffect(() => {
    if (!fuenteHabilitaEmpresa) {
      setEmpresaMenuAbierto(false);
      setEmpresaSearchTerm("");
    }
  }, [fuenteHabilitaEmpresa]);

  const buildReferidoLabel = (ref, seleccionadoId) => {
    const nombre = `${ref.nombres || ""} ${ref.apellidos || ""}`.trim();
    const documento = ref.documento_identidad || ref.identificacion || ref.documento || "";
    const estado = ref.estado_academico ? ` (${ref.estado_academico})` : ref.tipo ? ` (${ref.tipo})` : "";
    if (nombre || documento) return `${nombre || documento}${estado}`;
    if (seleccionadoId && String(ref.id) === String(seleccionadoId)) {
      const fallbackNombre = `${nuevoRegistro.referido_nombres || ""} ${nuevoRegistro.referido_apellidos || ""}`.trim();
      const fallbackDoc = (nuevoRegistro.referido_documento || "").trim();
      const fallback = fallbackNombre || fallbackDoc;
      if (fallback) return `${fallback}${estado}`;
    }
    return `ID ${ref.id}${estado}`;
  };

  const opcionesReferido = React.useMemo(() => {
    const opts = Array.isArray(referidosOpciones) ? [...referidosOpciones] : [];
    if (nuevoRegistro.referido_por_id) {
      const existe = opts.some((r) => String(r.id) === String(nuevoRegistro.referido_por_id));
      if (!existe) {
        opts.push({
          id: nuevoRegistro.referido_por_id,
          nombres: nuevoRegistro.referido_nombres || "",
          apellidos: nuevoRegistro.referido_apellidos || "",
          documento_identidad: nuevoRegistro.referido_documento || "",
          estado_academico: nuevoRegistro.referido_estado || "",
        });
      }
    }
    return opts;
  }, [
    referidosOpciones,
    nuevoRegistro.referido_por_id,
    nuevoRegistro.referido_nombres,
    nuevoRegistro.referido_apellidos,
    nuevoRegistro.referido_documento,
    nuevoRegistro.referido_estado,
  ]);

  const tipoReferido = resolveTipoReferido(normalizeKey(nuevoRegistro.fuente_origen));
  const opcionesReferidoFiltradas = React.useMemo(() => {
    if (!tipoReferido) return opcionesReferido;
    return opcionesReferido.filter((ref) => {
      if (nuevoRegistro.referido_por_id && String(ref.id) === String(nuevoRegistro.referido_por_id)) return true;
      const estado = normalizeKey(ref.estado_academico || ref.tipo || "");
      if (!estado) return true;
      if (tipoReferido === "docente") return estado.includes("docente");
      return estado.includes(tipoReferido);
    });
  }, [opcionesReferido, tipoReferido, nuevoRegistro.referido_por_id]);

  const referidoSeleccionado = opcionesReferidoFiltradas.find(
    (ref) => String(ref.id) === String(nuevoRegistro.referido_por_id || "")
  );
  const referidoSeleccionadoLabel = referidoSeleccionado
    ? buildReferidoLabel(referidoSeleccionado, nuevoRegistro.referido_por_id)
    : "";
  const referidoInputValue = referidoSearchTerm || referidoSeleccionadoLabel;
  const empresaSeleccionadaLabel = (nuevoRegistro.empresa || "").toString();
  const empresaInputValue = empresaSearchTerm || empresaSeleccionadaLabel;
  const empresasFiltradas = React.useMemo(() => {
    const term = normalizeKey(empresaSearchTerm);
    if (!term) return [];
    return (empresasLista || []).filter((emp) => normalizeKey(emp).includes(term));
  }, [empresaSearchTerm, empresasLista]);

  if (!show) return null;

  return (
    <div className="modal fade show d-block management-modal bitacora-modal" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content shadow">
          <div className="modal-header border-0">
            <div>
              <p className="dashboard-eyebrow mb-1">{editando ? "Edicion" : "Nuevo ingreso"}</p>
              <h5 className="modal-title">{editando ? "Editar registro" : "Registrar visita"}</h5>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <ul className="nav nav-tabs px-3 dashboard-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${tabActiva === "interesado" ? "active" : ""}`}
                onClick={() => setTabActiva("interesado")}
              >
                Interesado
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${tabActiva === "bitacora" ? "active" : ""}`}
                onClick={() => setTabActiva("bitacora")}
              >
                Bitacora
              </button>
            </li>
          </ul>

          <div className="modal-body">
            {tabActiva === "interesado" && (
              <div className="dashboard-form-section">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label dashboard-label">Cedula</label>
                    <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar por cedula"
                    value={nuevoRegistro.documento}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNuevoRegistro({ ...nuevoRegistro, documento: value });
                      if (bloquearInteresado && value !== documentoBloqueado) {
                        setBloquearInteresado(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        buscarInteresado(nuevoRegistro.documento);
                      }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => buscarInteresado(nuevoRegistro.documento)}
                      >
                        <i className="bi bi-search" />
                      </button>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label dashboard-label">Nombres</label>
                    <input
                      type="text"
                      className="form-control"
                      value={nuevoRegistro.nombre}
                      onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, nombre: e.target.value.toUpperCase() })}
                      disabled={bloquearInteresado}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label dashboard-label">Apellidos</label>
                    <input
                      type="text"
                      className="form-control"
                      value={nuevoRegistro.apellidos}
                      onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, apellidos: e.target.value.toUpperCase() })}
                      disabled={bloquearInteresado}
                    />
                  </div>
                </div>
                <div className="row g-3 mt-1">
                  <div className="col-md-4">
                    <label className="form-label dashboard-label">Telefono</label>
                    <input
                      type="text"
                      className="form-control"
                      value={nuevoRegistro.telefono}
                      onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, telefono: e.target.value })}
                      disabled={bloquearInteresado}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label dashboard-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={nuevoRegistro.email}
                      onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, email: e.target.value })}
                      disabled={bloquearInteresado}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label dashboard-label">Fuente de origen</label>
                    <select
                      className="form-select"
                      value={nuevoRegistro.fuente_origen}
                      onChange={(e) => {
                        const value = e.target.value;
                        const clave = normalizeKey(value);
                        const nextHabilitaReferido = ["administrativo", "estudiante", "docente"].includes(clave);
                        setNuevoRegistro((prev) => ({
                          ...prev,
                          fuente_origen: value,
                          referido_por_id: nextHabilitaReferido || requiereReferido ? prev.referido_por_id : null,
                        }));
                        if (!nextHabilitaReferido && !requiereReferido) {
                          setReferidoSearchTerm("");
                          setReferidoMenuAbierto(false);
                        }
                      }}
                      disabled={bloquearInteresado}
                    >
                      <option value="">Seleccione...</option>
                      {fuentesLista.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {fuenteHabilitaEmpresa && (
                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label className="form-label dashboard-label">Empresa</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            className="form-control"
                            placeholder="Escribe para buscar (min. 3)"
                            value={empresaInputValue}
                            onFocus={() => {
                              if ((empresaSearchTerm || "").trim().length >= 3) setEmpresaMenuAbierto(true);
                            }}
                            onBlur={() => {
                              setTimeout(() => setEmpresaMenuAbierto(false), 150);
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEmpresaSearchTerm(value);
                              setEmpresaMenuAbierto(value.trim().length >= 3);
                              setNuevoRegistro((prev) => ({
                                ...prev,
                                empresa: value,
                                nuevaEmpresa: { nombre: "" },
                              }));
                            }}
                            disabled={bloquearInteresado}
                          />
                          {!!empresaInputValue && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                setEmpresaSearchTerm("");
                                setEmpresaMenuAbierto(false);
                                setNuevoRegistro((prev) => ({
                                  ...prev,
                                  empresa: "",
                                  nuevaEmpresa: { nombre: "" },
                                }));
                              }}
                              disabled={bloquearInteresado}
                              title="Limpiar"
                            >
                              <i className="bi bi-x-lg" />
                            </button>
                          )}
                        </div>

                        {empresaMenuAbierto && (
                          <div className="list-group int-referido-autocomplete shadow-sm">
                            {empresaSearchTerm.trim().length < 3 ? (
                              <div className="list-group-item text-muted small">Ingresa al menos 3 caracteres.</div>
                            ) : empresasFiltradas.length > 0 ? (
                              empresasFiltradas.map((emp) => (
                                <button
                                  key={emp}
                                  type="button"
                                  className="list-group-item list-group-item-action int-referido-option"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setNuevoRegistro((prev) => ({
                                      ...prev,
                                      empresa: emp,
                                      nuevaEmpresa: { nombre: "" },
                                    }));
                                    setEmpresaSearchTerm("");
                                    setEmpresaMenuAbierto(false);
                                  }}
                                >
                                  {emp}
                                </button>
                              ))
                            ) : (
                              <div className="list-group-item text-muted small">
                                No hay coincidencias. Se guardara como nueva empresa.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {(fuenteHabilitaReferido || requiereReferido || nuevoRegistro.referido_por_id) && (
                  <div className="col-12">
                    <label className="form-label dashboard-label">Referido</label>
                    <div className="position-relative">
                      <div className="input-group">
                        <input
                          className="form-control"
                          placeholder="Escribe para buscar (min. 2)"
                          value={referidoInputValue}
                          onFocus={() => {
                            if ((referidoSearchTerm || "").trim()) setReferidoMenuAbierto(true);
                          }}
                          onBlur={() => {
                            setTimeout(() => setReferidoMenuAbierto(false), 150);
                          }}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setReferidoSearchTerm(nextValue);
                            setReferidoMenuAbierto(true);
                            if (
                              nuevoRegistro.referido_por_id &&
                              nextValue.trim() !== referidoSeleccionadoLabel.trim()
                            ) {
                              setNuevoRegistro((prev) => ({ ...prev, referido_por_id: null }));
                            }
                          }}
                          disabled={bloquearInteresado}
                        />
                        {!!referidoInputValue && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              setReferidoSearchTerm("");
                              setReferidoMenuAbierto(false);
                              setNuevoRegistro((prev) => ({ ...prev, referido_por_id: null }));
                            }}
                            disabled={bloquearInteresado}
                            title="Limpiar"
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        )}
                        {cargandoReferidos && (
                          <span className="input-group-text">
                            <span className="spinner-border spinner-border-sm" role="status" />
                          </span>
                        )}
                      </div>

                      {referidoMenuAbierto && (
                        <div className="list-group int-referido-autocomplete shadow-sm">
                          {opcionesReferidoFiltradas.length > 0 ? (
                            opcionesReferidoFiltradas.map((ref) => {
                              const label = buildReferidoLabel(ref, nuevoRegistro.referido_por_id);
                              return (
                                <button
                                  key={ref.id}
                                  type="button"
                                  className={`list-group-item list-group-item-action int-referido-option ${
                                    String(ref.id) === String(nuevoRegistro.referido_por_id) ? "active" : ""
                                  }`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setNuevoRegistro((prev) => ({ ...prev, referido_por_id: String(ref.id) }));
                                    setReferidoSearchTerm("");
                                    setReferidoMenuAbierto(false);
                                  }}
                                >
                                  {label}
                                </button>
                              );
                            })
                          ) : (
                            <div className="list-group-item text-muted small">
                              {referidoInputValue.trim().length < 2
                                ? "Ingresa al menos 2 caracteres."
                                : "Sin coincidencias para el termino ingresado."}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tabActiva === "bitacora" && (
              <div className="row g-3 dashboard-form-section">
                <div className="col-12">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label dashboard-label">Categoria</label>
                      <select
                        className="form-select"
                        value={selectedCategoria}
                        onChange={(e) => {
                          setSelectedCategoria(e.target.value);
                          setSelectedSubcategoria("");
                          setSelectedAccion("");
                        }}
                      >
                        <option value="">Seleccione...</option>
                        {categoriasLista.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label dashboard-label">Subcategoria</label>
                      <select
                        className="form-select"
                        value={selectedSubcategoria}
                        onChange={(e) => {
                          setSelectedSubcategoria(e.target.value);
                          setSelectedAccion("");
                        }}
                        disabled={!selectedCategoria}
                      >
                        <option value="">Seleccione...</option>
                        {subcategoriasLista.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label dashboard-label">Accion</label>
                      <select
                        className="form-select"
                        value={selectedAccion}
                        onChange={(e) => setSelectedAccion(e.target.value)}
                        disabled={!selectedSubcategoria}
                      >
                        <option value="">Seleccione...</option>
                        {accionesLista.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="col-12 dashboard-divider"></div>

                <div className="col-md-12">
                  <div className="d-flex gap-4 flex-wrap align-items-center">
                    {showPreinscribir && (
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={nuevoRegistro.convertido_a_prematricula}
                          id="prematriculaCheck"
                          onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, convertido_a_prematricula: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="prematriculaCheck">
                          Pre-inscribir
                        </label>
                      </div>
                    )}
                    {showTransfer && (
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={nuevoRegistro.transferido}
                          id="transferidoCheck"
                          onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, transferido: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="transferidoCheck">
                          Transferir a secretaria
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label dashboard-label">Observacion</label>
                  <textarea
                    className="form-control"
                    rows="5"
                    value={nuevoRegistro.observacion}
                    onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, observacion: e.target.value })}
                  ></textarea>
                </div>

                {puedeEditarFecha && !editando && (
                  <div className="col-12">
                    <div className="p-2 border rounded bg-warning-subtle">
                      <label className="form-label dashboard-label mb-1">Fecha de visita (migración)</label>
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={nuevoRegistro.fecha_visita_override || ""}
                        onChange={(e) => setNuevoRegistro({ ...nuevoRegistro, fecha_visita_override: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer border-0">
            <button className="btn btn-outline-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn management-btn-primary" onClick={handleGuardar} disabled={guardando}>
              {guardando ? (editando ? "Actualizando..." : "Guardando...") : editando ? "Actualizar" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
